import { Injectable } from '@angular/core';
import { SessionContextService } from '../tracker/session-context.service';
import { ApiService } from '../api/api.service';
import { ConfigService } from '../config/config.service';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import { BrowserStorage } from '../../models/browserStorage';

interface Segment {
	videoFrom: number;
	videoTo: number;
	startedAt: number;
	endedAt: number;
	watchedSeconds: number;
	hasReachedEnd: boolean;
}

interface VideoSession {
	sessionId: string;
	courseSessionId: string;
	chapterSessionId: string;
	categoryId: string;
	courseId: string;
	chapterId: string;
	videoId: string;
	startedAt: number;
}

interface SegmentStart {
	videoTime: number;
	wallClock: number;
}

@Injectable({
	providedIn: 'root',
})
export class VideoTrackerService {
	private currentSession: VideoSession | null = null;
	private pendingSegments: Segment[] = [];
	private segmentStart: SegmentStart | null = null;
	private flushTimer: any = null;

	private readonly FLUSH_INTERVAL_MS = 30_000;

	constructor(
		private configService: ConfigService,
		private authService: AuthService,
		private sessionContext: SessionContextService,
		private api: ApiService,
	) {}

	private get isActive(): boolean {
		return (
			this.configService.isFeatureFlagEnabled('activityTrackingFeatureFlag', [this.authService.auth?.user?.id]) &&
			!!this.sessionContext.courseSessionId &&
			!!this.sessionContext.chapterSessionId
		);
	}

	startSession(videoId: string): void {
		if (!this.isActive || !videoId) {
			return;
		}

		if (this.currentSession) {
			this.endSession();
		}

		this.currentSession = {
			sessionId: crypto.randomUUID(),
			courseSessionId: this.sessionContext.courseSessionId!,
			chapterSessionId: this.sessionContext.chapterSessionId!,
			categoryId: this.sessionContext.categoryId!,
			courseId: this.sessionContext.courseId!,
			chapterId: this.sessionContext.chapterId!,
			videoId,
			startedAt: Date.now(),
		};
		this.pendingSegments = [];
		this.segmentStart = null;
		this.scheduleFlush();
	}

	onPlay(currentVideoTime: number): void {
		if (!this.currentSession) {
			return;
		}

		if (this.segmentStart) {
			this.closeSegment(currentVideoTime);
		}

		this.segmentStart = {
			videoTime: currentVideoTime,
			wallClock: Date.now(),
		};
	}

	onPause(currentVideoTime: number): void {
		if (!this.currentSession) {
			return;
		}

		this.closeSegment(currentVideoTime);
		this.flush();
	}

	onSeeking(currentVideoTime: number): void {
		if (!this.currentSession || !this.segmentStart) {
			return;
		}

		this.closeSegment(currentVideoTime);
	}

	onSeeked(newVideoTime: number, isPlaying: boolean): void {
		if (!this.currentSession) {
			return;
		}

		if (isPlaying) {
			this.segmentStart = {
				videoTime: newVideoTime,
				wallClock: Date.now(),
			};
		}
	}

	onVideoEnd(currentVideoTime: number): void {
		if (!this.currentSession) {
			return;
		}

		this.closeSegment(currentVideoTime, true);
		this.endSession();
	}

	flushViaBeacon(): void {
		if (!this.currentSession) {
			return;
		}

		if (this.segmentStart) {
			this.closeSegment(this.segmentStart.videoTime);
		}

		if (this.pendingSegments.length === 0) {
			return;
		}

		const payload = {
			sessionId: this.currentSession.sessionId,
			courseSessionId: this.currentSession.courseSessionId,
			chapterSessionId: this.currentSession.chapterSessionId,
			categoryId: this.currentSession.categoryId,
			courseId: this.currentSession.courseId,
			chapterId: this.currentSession.chapterId,
			videoId: this.currentSession.videoId,
			startedAt: this.currentSession.startedAt,
			segments: this.pendingSegments
		};

		this.pendingSegments = [];

		fetch(`${environment.API_URL}/tracker/video/flush`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': BrowserStorage.get('userToken')
			},
			body: JSON.stringify(payload),
			keepalive: true
		});
	}

	private closeSegment(currentVideoTime: number, hasReachedTheEnd: boolean = false): void {
		if (!this.segmentStart) {
			return;
		}

		const now = Date.now();
		const watchedSeconds = Math.round((now - this.segmentStart.wallClock) / 1000);

		if (watchedSeconds <= 0) {
			this.segmentStart = null;
			return;
		}

		this.pendingSegments.push({
			videoFrom: this.segmentStart.videoTime,
			videoTo: currentVideoTime,
			startedAt: this.segmentStart.wallClock,
			endedAt: now,
			watchedSeconds,
			hasReachedEnd: hasReachedTheEnd
		});

		this.segmentStart = null;
	}

	private scheduleFlush(): void {
		if (this.flushTimer) {
			clearInterval(this.flushTimer);
		}
		this.flushTimer = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);
	}

	async flush(): Promise<void> {
		if (!this.currentSession || this.pendingSegments.length === 0) {
			return;
		}

		const flushing = [...this.pendingSegments];
		this.pendingSegments = [];

		const payload = {
			sessionId: this.currentSession.sessionId,
			courseSessionId: this.currentSession.courseSessionId,
			chapterSessionId: this.currentSession.chapterSessionId,
			categoryId: this.currentSession.categoryId,
			courseId: this.currentSession.courseId,
			chapterId: this.currentSession.chapterId,
			videoId: this.currentSession.videoId,
			startedAt: this.currentSession.startedAt,
			segments: flushing,
		};

		try {
			await this.api.post('/tracker/video/flush', payload);
		} catch (err) {
			console.error('Failed to flush video activity', err);
			this.pendingSegments.unshift(...flushing);
		}
	}

	endSession(): void {
		if (this.flushTimer) {
			clearInterval(this.flushTimer);
			this.flushTimer = null;
		}

		if (this.segmentStart) {
			this.closeSegment(this.segmentStart.videoTime);
		}

		this.flush();
		this.currentSession = null;
	}
}
